import { useState } from 'react'


export interface UseInputHookProps {
    onChange: (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
    value: string
    onFocus?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
    onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
    max?: number,
    min? : number
}
const defaultOnChange = (v: string) => v
export const useInput = (config?: {
    initValue?: string
    onChange?: (value: string) => string
    onFocus?: () => void
    onBlur?: () => void
}): [string, UseInputHookProps, (value: string) => void] => {
    const { initValue = '', onChange: onChangeProp = defaultOnChange, onFocus, onBlur } = config ?? {}
    const [value, setValue] = useState(initValue)

    function onChange(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) {
        var value = e.currentTarget.value
        injectValue(value)
    }

    function injectValue(value: string) {
        setValue(onChangeProp(value))
    }

    return [value, { onChange, value, onFocus, onBlur }, injectValue]
}


export type Option = string | number
export interface UseSelectHookProps {
    onChange: (e: React.FormEvent<HTMLSelectElement>) => void,
    options: ({ value: Option, label?: string } | Option)[]
    value: string
}
export const useSelect = (config?: {
    initValue?: string
    options?: ({ value: Option, label?: string } | Option)[]
    onChange?: (value: string) => string
}): [string, UseSelectHookProps, (value: string) => void] => {
    const { initValue = '', options = [], onChange: onChangeProp = defaultOnChange } = config ?? {}
    const [value, setValue] = useState(initValue)

    function onChange(e: React.FormEvent<HTMLSelectElement>) {
        var value = e.currentTarget.value
        injectValue(value)
    }

    function injectValue(value: string) {
        setValue(onChangeProp(value))
    }

    return [value, { onChange, options, value }, injectValue]
}


export interface UseRadioHookProps {
    onChange: (e: React.FormEvent<HTMLInputElement>) => void,
}
export const useRadio = (config?: {
    initValue?: string
    onChange?: (value: string) => string
}): [string, UseRadioHookProps, (value: string) => void] => {
    const { initValue = '', onChange: onChangeProp = defaultOnChange } = config ?? {}
    const [value, setValue] = useState(initValue)

    function onChange(e: React.FormEvent<HTMLInputElement>) {
        if (e.currentTarget.checked) {
            var value = e.currentTarget.value
            injectValue(value)
        }
    }

    function injectValue(value: string) {
        setValue(onChangeProp(value))
    }

    return [value, { onChange }, injectValue]
}


export const useCurrencyInput = (config?: {
    initValue?: string
    max?: number
    min?: number
    onChange?: (value: string) => string
    onFocus?: () => void
    onBlur?: (amount: number) => void
}): [string, UseInputHookProps, (value: string) => void] => {
    const { initValue = '', max, min, onChange: onChangeProp = defaultOnChange, onFocus, onBlur: onBlurProps } = config ?? {}
    const [value, setValue] = useState(toUSD(initValue, undefined, max))

    function onChange(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) {
        var v = e.currentTarget.value
        injectValue(v)
    }

    function injectValue(v: string) {
        setValue(onChangeProp(toUSD(v, value)))
    }

    function onBlur() {
        if (onBlurProps) onBlurProps(parseNum(value))
    }

    return [value, { onChange, value, onFocus, onBlur, max, min }, injectValue]
}
const numberFormatter = new Intl.NumberFormat('en-US')
export const toUSD = (value: string, prevValue?: string, max?: number, forceTwoDecimals?: boolean): string => {
    if (value.at(-1) === '.' && (!!prevValue && prevValue.at(-1) !== '.')) return value
    const [whole, fraction] = value.split('.').map((seg) => seg.replace(/[^0-9]/g, ''))

    if (!Number(whole) && !fraction) return ''
    else if (max != null && (Number(whole) * 100) > max) return numberFormatter.format(max / 100)
    else if (forceTwoDecimals) return `${numberFormatter.format(Number(whole) || 0)}${fraction != null ? `.${fraction.at(0) ?? '0'}${fraction.at(1) ?? '0'}` : '.00'}`
    else return `${numberFormatter.format(Number(whole) || 0)}${fraction != null ? `.${fraction.at(0) ?? ''}${fraction.at(1) ?? ''}` : ''}`
}
const parseNum = (value: string) => {
    const [whole, fraction] = value.split('.').map((seg) => seg.replace(/[^0-9]/g, ''))
    return Number(`${whole}.${fraction?.at(0) || 0}${fraction?.at(1) || 0}`) || 0
}


const defaultOnCheckProp = (v: boolean) => v
export const useCheckbox = (config?: {
    initValue?: boolean,
    onChange?: (isChecked: boolean) => boolean
}): [boolean, { checked: boolean, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }, (value: boolean) => void] => {
    const { initValue = false, onChange: onChangeProp = defaultOnCheckProp } = config ?? {}
    const [checked, setChecked] = useState(initValue)

    function onChange(e: React.ChangeEvent<HTMLInputElement>) {
        injectValue(e.currentTarget.checked)
    }

    function injectValue(value: boolean) {
        setChecked(onChangeProp(value))
    }

    return [checked, { checked, onChange }, injectValue]
}